import api from '@/libs/axiosInstance';
import type {
  ApiResponse,
  RequestBrandSignupCodeRequest,
  RequestBrandSignupCodeResponse,
} from '@fluenceur/types';
import { useMutation } from '@tanstack/react-query';
import { metaPixel } from '@/libs/meta-pixel';
import { productAnalytics } from '@/libs/product-analytics';

/**
 * Sends a six-digit code to the address a brand signs up with
 * (`POST /auth/brands/signup-code`). The account is only created once the code
 * comes back with `POST /auth/brands/register`. Refused with a 409 for an
 * address that cannot become a brand account, and a 429 when codes were asked
 * for too often.
 */
export const useRequestBrandSignupCodeAction = () =>
  useMutation<RequestBrandSignupCodeResponse, Error, RequestBrandSignupCodeRequest>({
    mutationFn: async (payload) => {
      productAnalytics.capture('authentication_started', {
        feature: 'authentication',
        action: 'create',
        entry_point: 'self_serve',
      });
      const { data } = await api.post<ApiResponse<RequestBrandSignupCodeResponse>>(
        '/auth/brands/signup-code',
        payload
      );
      return data.data as RequestBrandSignupCodeResponse;
    },
    // The address is known before the account exists: the closest thing to a lead here.
    onSuccess: () => metaPixel.track('Lead', { content_name: 'Brand signup' }),
  });
